/**
 * Cycles a bottom-left FOMO popup through catalog products with a random
 * US buyer, city, and recent timestamp.
 */

/** @typedef {{ title: string, url: string, image: string }} FomoProduct */

const DISMISS_KEY = 'sd-fomo-dismissed';
const SHOW_MS = 7000;
const MIN_GAP_MS = 11000;
const MAX_GAP_MS = 18000;
const FIRST_DELAY_MS = 4200;

const FIRST_NAMES = [
  'Alex',
  'Jordan',
  'Maya',
  'Chris',
  'Taylor',
  'Riley',
  'Sam',
  'Casey',
  'Nina',
  'Marcus',
  'Ava',
  'Derek',
  'Priya',
  'Luis',
  'Keisha',
  'Noah',
  'Elena',
  'Jamal',
  'Sofia',
  'Brett',
  'Aisha',
  'Tyler',
  'Mei',
  'Andre',
  'Harper',
  'Diego',
  'Quinn',
  'Imani',
  'Cole',
  'Leah',
];

const LAST_INITIALS = [
  'A',
  'B',
  'C',
  'D',
  'F',
  'G',
  'H',
  'J',
  'K',
  'L',
  'M',
  'N',
  'P',
  'R',
  'S',
  'T',
  'W',
];

const CITIES = [
  'Austin, TX',
  'Brooklyn, NY',
  'Los Angeles, CA',
  'Chicago, IL',
  'Miami, FL',
  'Atlanta, GA',
  'Denver, CO',
  'Seattle, WA',
  'Phoenix, AZ',
  'Dallas, TX',
  'Portland, OR',
  'Nashville, TN',
  'Boston, MA',
  'Philadelphia, PA',
  'San Diego, CA',
  'Houston, TX',
  'Detroit, MI',
  'Minneapolis, MN',
  'New Orleans, LA',
  'Charlotte, NC',
  'Las Vegas, NV',
  'Tampa, FL',
  'Columbus, OH',
  'Kansas City, MO',
  'Salt Lake City, UT',
  'Raleigh, NC',
  'Oakland, CA',
  'Pittsburgh, PA',
  'Baltimore, MD',
  'Sacramento, CA',
];

/**
 * @param {unknown} value
 * @returns {value is FomoProduct}
 */
function isFomoProduct(value) {
  if (typeof value !== 'object' || value === null) return false;
  const item = /** @type {Record<string, unknown>} */ (value);
  return typeof item.title === 'string' && typeof item.url === 'string' && typeof item.image === 'string';
}

/**
 * @template T
 * @param {T[]} list
 * @returns {T}
 */
function pick(list) {
  const item = list[Math.floor(Math.random() * list.length)];
  if (item === undefined) {
    throw new Error('FOMO popup: cannot pick from an empty list');
  }
  return item;
}

/**
 * @template T
 * @param {T[]} list
 * @returns {T[]}
 */
function shuffled(list) {
  const copy = list.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const current = copy[i];
    const swap = copy[j];
    if (current === undefined || swap === undefined) continue;
    copy[i] = swap;
    copy[j] = current;
  }
  return copy;
}

function randomMinutesAgo() {
  const roll = Math.random();
  if (roll < 0.42) return Math.floor(Math.random() * 8);
  if (roll < 0.78) return 8 + Math.floor(Math.random() * 22);
  return 30 + Math.floor(Math.random() * 51);
}

/**
 * @param {number} minutes
 */
function formatTimeAgo(minutes) {
  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.max(1, Math.round(minutes / 60));
  return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
}

function randomGap() {
  return MIN_GAP_MS + Math.floor(Math.random() * (MAX_GAP_MS - MIN_GAP_MS));
}

function initFomoPopup() {
  if (sessionStorage.getItem(DISMISS_KEY) === '1') return;

  const root = document.getElementById('sd-fomo');
  const dataEl = document.getElementById('fomo-popup-products');
  if (!(root instanceof HTMLElement) || !(dataEl instanceof HTMLElement)) return;

  /** @type {unknown} */
  let parsed;
  try {
    parsed = JSON.parse(dataEl.textContent || '[]');
  } catch {
    return;
  }

  if (!Array.isArray(parsed)) return;
  const products = parsed.filter(isFomoProduct);
  if (products.length === 0) return;

  const nameEl = root.querySelector('[data-fomo-name]');
  const titleEl = root.querySelector('[data-fomo-title]');
  const cityEl = root.querySelector('[data-fomo-city]');
  const timeEl = root.querySelector('[data-fomo-time]');
  const imageEl = root.querySelector('[data-fomo-image]');
  const closeEl = root.querySelector('[data-fomo-close]');
  const links = root.querySelectorAll('[data-fomo-link]');

  if (
    !(nameEl instanceof HTMLElement) ||
    !(titleEl instanceof HTMLAnchorElement) ||
    !(cityEl instanceof HTMLElement) ||
    !(timeEl instanceof HTMLElement) ||
    !(imageEl instanceof HTMLImageElement) ||
    !(closeEl instanceof HTMLButtonElement)
  ) {
    return;
  }

  let queue = shuffled(products);
  let index = 0;
  /** @type {number | undefined} */
  let hideTimer;
  /** @type {number | undefined} */
  let showTimer;
  let hovering = false;
  let dismissed = false;
  let visible = false;

  function nextProduct() {
    const product = queue[index] ?? products[0];
    index += 1;
    if (index >= queue.length) {
      queue = shuffled(products);
      index = 0;
      const first = queue[0];
      if (products.length > 1 && first && product && first.url === product.url) {
        index = 1;
      }
    }
    return product;
  }

  function fill() {
    const product = nextProduct();
    if (!product) return;

    const buyer = `${pick(FIRST_NAMES)} ${pick(LAST_INITIALS)}.`;
    nameEl.textContent = buyer;
    titleEl.textContent = product.title;
    cityEl.textContent = pick(CITIES);
    timeEl.textContent = formatTimeAgo(randomMinutesAgo());
    imageEl.src = product.image;
    imageEl.alt = product.title;

    for (const link of links) {
      if (link instanceof HTMLAnchorElement) {
        link.href = product.url;
      }
    }
  }

  function show() {
    if (dismissed || hovering || document.hidden) {
      if (!dismissed) showTimer = window.setTimeout(show, randomGap());
      return;
    }

    fill();
    visible = true;
    root.classList.add('is-visible');
    root.setAttribute('aria-hidden', 'false');
    hideTimer = window.setTimeout(hide, SHOW_MS);
  }

  function hide() {
    visible = false;
    root.classList.remove('is-visible');
    root.setAttribute('aria-hidden', 'true');
    if (dismissed || hovering) return;
    showTimer = window.setTimeout(show, randomGap());
  }

  function dismiss() {
    dismissed = true;
    sessionStorage.setItem(DISMISS_KEY, '1');
    window.clearTimeout(hideTimer);
    window.clearTimeout(showTimer);
    visible = false;
    root.classList.remove('is-visible');
    root.setAttribute('aria-hidden', 'true');
  }

  closeEl.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    dismiss();
  });

  root.addEventListener('mouseenter', () => {
    if (!visible) return;
    hovering = true;
    window.clearTimeout(hideTimer);
  });

  root.addEventListener('mouseleave', () => {
    if (dismissed) return;
    hovering = false;
    if (visible) {
      hideTimer = window.setTimeout(hide, 2500);
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      window.clearTimeout(hideTimer);
      window.clearTimeout(showTimer);
      if (visible) {
        visible = false;
        root.classList.remove('is-visible');
        root.setAttribute('aria-hidden', 'true');
      }
      return;
    }
    if (dismissed) return;
    hovering = false;
    showTimer = window.setTimeout(show, randomGap());
  });

  showTimer = window.setTimeout(show, FIRST_DELAY_MS);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFomoPopup);
} else {
  initFomoPopup();
}
